import { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Plus } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import './MultiImageUpload.css';

const MultiImageUpload = ({ value = [], onChange, label = 'Images', required = false, maxImages = 10, uploadImage }) => {
  const initialValue = Array.isArray(value) ? value : (value ? [value] : []);
  const [previews, setPreviews] = useState(initialValue);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  // Sync with value prop changes
  useEffect(() => {
    const newValue = Array.isArray(value) ? value : (value ? [value] : []);
    setPreviews(newValue);
  }, [value]);

  const gridRef = useRef(null);
  
  useEffect(() => {
    if (!gridRef.current) return;
    import('sortablejs').then((module) => {
      const Sortable = module.default;
      const sortable = Sortable.create(gridRef.current, {
        animation: 150,
        handle: '.drag-handle',
        onEnd: (evt) => {
          if (evt.oldIndex === evt.newIndex) return;
          setPreviews((prev) => {
            const list = [...prev];
            const [moved] = list.splice(evt.oldIndex, 1);
            list.splice(evt.newIndex, 0, moved);
            onChange(list);
            return list;
          });
        }
      });
      return () => sortable.destroy();
    });
  }, [previews.length, onChange]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max images limit
    if (previews.length + files.length > maxImages) {
      showToast({
        message: `Maximum ${maxImages} images allowed. You can add ${maxImages - previews.length} more.`,
        severity: 'warning'
      });
      return;
    }

    setIsUploading(true);

    try {
      const newImages = [];
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          showToast({ message: `${file.name} is not an image file`, severity: 'warning' });
          continue;
        }

        if (typeof uploadImage === 'function') {
          const url = await uploadImage(file);
          if (url) newImages.push(url);
        } else {
          const base64String = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          newImages.push(base64String);
        }
      }

      const updatedPreviews = [...previews, ...newImages];
      setPreviews(updatedPreviews);
      onChange(updatedPreviews);
      setIsUploading(false);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      showToast({
        message: error.response?.data?.message || error.message || 'Error uploading images',
        severity: 'error'
      });
      setIsUploading(false);
    }
  };

  const handleRemove = (index) => {
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setPreviews(updatedPreviews);
    onChange(updatedPreviews);
  };

  const handleUrlAdd = (url) => {
    if (!url.trim()) return;
    
    if (previews.length >= maxImages) {
      showToast({ message: `Maximum ${maxImages} images allowed`, severity: 'warning' });
      return;
    }

    const updatedPreviews = [...previews, url.trim()];
    setPreviews(updatedPreviews);
    onChange(updatedPreviews);
  };

  const handleUrlInput = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUrlAdd(e.target.value);
      e.target.value = '';
    }
  };

  return (
    <div className="multi-image-upload-container">
      <label className="multi-image-upload-label">
        {label} {required && <span className="required">*</span>}
        {previews.length > 0 && (
          <span className="image-count">({previews.length}/{maxImages})</span>
        )}
      </label>

      {/* Image Grid */}
      {previews.length > 0 && (
        <div className="image-grid" ref={gridRef}>
          {previews.map((preview, index) => (
            <div key={index} className="image-preview-item drag-handle" style={{ cursor: 'grab' }}>
              <div className="image-preview-wrapper group">
                <img src={preview} alt={`Preview ${index + 1}`} className="preview-image" />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                  title="Remove image"
                >
                  <X size={16} />
                </button>
                <div className="image-index">{index + 1}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {previews.length < maxImages && (
        <div className="multi-image-upload-area">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="multi-image-upload-input"
            id="multi-image-upload"
            disabled={isUploading}
          />
          <label htmlFor="multi-image-upload" className="multi-image-upload-dropzone">
            <Upload size={28} className="upload-icon" />
            <p className="upload-text">
              {isUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </p>
            <p className="upload-hint">PNG, JPG, GIF</p>
            <p className="upload-hint-small">You can select multiple images</p>
          </label>
        </div>
      )}



      {previews.length >= maxImages && (
        <p className="max-images-message">Maximum {maxImages} images reached</p>
      )}
    </div>
  );
};

export default MultiImageUpload;
